import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Layout, Menu, Button, theme, Avatar, Dropdown, Badge, Spin, Select, notification, message } from 'antd';
import type { MenuProps } from 'antd';
import {
  DashboardOutlined,
  ShoppingOutlined,
  InboxOutlined,
  ShoppingCartOutlined,
  UserOutlined,
  SettingOutlined,
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  BellOutlined,
  LogoutOutlined,
  TeamOutlined,
  CloudOutlined,
  UsergroupAddOutlined,
  BarChartOutlined,
  ShopOutlined,
  FileSearchOutlined,
  ContactsOutlined,
  ProfileOutlined,
  MobileOutlined,
  PrinterOutlined,
  GiftOutlined,
  TrophyOutlined,
  DatabaseOutlined,
  PercentageOutlined,
  WhatsAppOutlined,
  CarOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import { logout } from '../../store/slices/authSlice';
import { getLowStockCount } from '../../api/stock';
import { getStores, getStoresFromApiKeys } from '../../api/store';
import { getPurchaseImportJob } from '../../api/purchase';
import { getReceivableAlerts, getChecksSummary } from '../../api/finance';
import {
  listTrackedPurchaseImportJobs,
  PURCHASE_IMPORT_JOB_EVENT,
  untrackPurchaseImportJob,
} from '../../utils/purchaseImportJobTracker';
import { hasAnyRole, getUserRoleCode } from '../../utils/roleUtils';

// 判断页面是否被允许（null = 全部允许，[] = 全部禁止，[...] = 白名单）
function isPageAllowed(page: string, allowedPages: string[] | null): boolean {
  if (!Array.isArray(allowedPages)) return true;
  if (allowedPages.length === 0) return false;
  return allowedPages.includes(page);
}

type GenericMenuItem = Record<string, any>;

function buildSafeMenuItems(items: GenericMenuItem[]): MenuProps['items'] {
  const usedKeys = new Set<string>();

  const normalize = (list: GenericMenuItem[], parentKey: string): any[] => {
    return list.map((item, index) => {
      const rawKey = typeof item?.key === 'string' || typeof item?.key === 'number'
        ? String(item.key).trim()
        : '';
      const baseKey = rawKey || `${parentKey}-${index}`;
      let key = baseKey;
      let suffix = 1;
      while (usedKeys.has(key)) {
        key = `${baseKey}__${suffix++}`;
      }
      usedKeys.add(key);

      const safeItem: any = {
        key,
        label: item.label,
      };

      if (item.icon !== undefined) safeItem.icon = item.icon;
      if (item.type !== undefined) safeItem.type = item.type;
      if (item.disabled !== undefined) safeItem.disabled = Boolean(item.disabled);
      if (item.danger !== undefined) safeItem.danger = Boolean(item.danger);
      if (item.title !== undefined) safeItem.title = item.title;
      if (Array.isArray(item.children) && item.children.length > 0) {
        safeItem.children = normalize(item.children, key);
      }

      return safeItem as any;
    });
  };

  return normalize(items, 'menu');
}

const { Header, Sider, Content } = Layout;
const GLOBAL_STORE_STORAGE_KEY = 'selectedStoreId';
const GLOBAL_STORE_CHANGE_EVENT = 'global-store-changed';

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [lowStockCount, setLowStockCount] = useState<number>(0);
  const [receivableAlertCount, setReceivableAlertCount] = useState<number>(0);
  const [, setCheckAlertCount] = useState<number>(0);
  const [stores, setStores] = useState<{ id: number, name: string }[]>([]);
  const [selectedGlobalStoreId, setSelectedGlobalStoreId] = useState<number | undefined>(undefined);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const trackedJobsRef = useRef<Set<string>>(new Set());
  const pollingRef = useRef<number | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  const { token: colorToken } = theme.useToken();
  const { user, isLoading } = useSelector((state: RootState) => state.auth || { user: null, isLoading: false });
  const { allowedPages } = useSelector((state: RootState) => state.activation);
  const authUser = user as (typeof user & {
    isSuperAdmin?: boolean;
    role_code?: string;
    permissions?: string[];
  }) | null;
  const isSuperAdmin = Boolean(
    authUser?.isSuperAdmin ||
    authUser?.role_code === 'super_admin' ||
    authUser?.permissions?.includes('*')
  );

  const loadStores = useCallback(async () => {
    try {
      const storesData = isSuperAdmin
        ? await getStores()
        : await getStoresFromApiKeys();
      const normalizedStores = isSuperAdmin
        ? [{ id: 0, name: '全部店铺' }, ...storesData]
        : storesData;
      setStores(normalizedStores);

      const rawSavedStoreId = localStorage.getItem(GLOBAL_STORE_STORAGE_KEY);
      const savedStoreId =
        rawSavedStoreId === 'all'
          ? (isSuperAdmin ? 0 : NaN)
          : (rawSavedStoreId ? parseInt(rawSavedStoreId, 10) : NaN);
      const hasSavedStore = Number.isFinite(savedStoreId) && normalizedStores.some((store) => store.id === savedStoreId);
      const hasCurrentStore = Number.isFinite(selectedGlobalStoreId) && normalizedStores.some((store) => store.id === selectedGlobalStoreId);
      const nextStoreId = hasSavedStore
        ? savedStoreId
        : (hasCurrentStore ? selectedGlobalStoreId : normalizedStores[0]?.id);

      if (nextStoreId === 0 || (typeof nextStoreId === 'number' && nextStoreId > 0)) {
        if (nextStoreId !== selectedGlobalStoreId) {
          setSelectedGlobalStoreId(nextStoreId);
          localStorage.setItem(GLOBAL_STORE_STORAGE_KEY, nextStoreId === 0 ? 'all' : String(nextStoreId));
          window.dispatchEvent(new CustomEvent(GLOBAL_STORE_CHANGE_EVENT, { detail: { value: nextStoreId } }));
        }
      } else if (selectedGlobalStoreId !== undefined) {
        setSelectedGlobalStoreId(undefined);
        localStorage.removeItem(GLOBAL_STORE_STORAGE_KEY);
      }
    } catch (error) {
      console.error('获取店铺列表失败:', error);
    }
  }, [isSuperAdmin, selectedGlobalStoreId]);

  useEffect(() => {
    let active = true;
    const fetchLowStockCount = async () => {
      if (!selectedGlobalStoreId) {
        if (active) setLowStockCount(0);
        return;
      }
      try {
        const total = await getLowStockCount(selectedGlobalStoreId);
        if (active) {
          setLowStockCount(total);
        }
      } catch (error) {
        console.error('获取低库存数量失败', error);
        if (active) {
          setLowStockCount(0);
        }
      }
    };

    fetchLowStockCount();

    const intervalId = setInterval(fetchLowStockCount, 10 * 60 * 1000);

    return () => {
      active = false;
      clearInterval(intervalId);
    };
  }, [selectedGlobalStoreId]);

  // 获取应收账款和支票预警数
  useEffect(() => {
    let active = true;
    const fetchFinanceAlerts = async () => {
      if (!selectedGlobalStoreId || selectedGlobalStoreId === 0) {
        if (active) { setReceivableAlertCount(0); setCheckAlertCount(0); }
        return;
      }
      try {
        const [recAlerts, chkSummary] = await Promise.all([
          getReceivableAlerts(selectedGlobalStoreId).catch(() => null),
          getChecksSummary(selectedGlobalStoreId).catch(() => null),
        ]);
        if (!active) return;
        setReceivableAlertCount((recAlerts?.overdueCount || 0) + (recAlerts?.nearDueCount || 0));
        setCheckAlertCount((chkSummary?.overdue?.count || 0) + (chkSummary?.near_due?.count || 0));
      } catch {
        if (active) { setReceivableAlertCount(0); setCheckAlertCount(0); }
      }
    };
    fetchFinanceAlerts();
    const intervalId = setInterval(fetchFinanceAlerts, 2 * 60 * 1000);
    window.addEventListener('focus', fetchFinanceAlerts);
    return () => { active = false; clearInterval(intervalId); window.removeEventListener('focus', fetchFinanceAlerts); };
  }, [selectedGlobalStoreId]);

  useEffect(() => {
    loadStores();

    const intervalId = window.setInterval(loadStores, 30 * 1000);
    const handleFocus = () => loadStores();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadStores();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loadStores]);

  useEffect(() => {
    const syncSelectedStore = () => {
      const rawSavedStoreId = localStorage.getItem(GLOBAL_STORE_STORAGE_KEY);
      if (rawSavedStoreId === 'all') {
        if (isSuperAdmin) {
          setSelectedGlobalStoreId(0);
        }
        return;
      }
      const savedStoreId = rawSavedStoreId ? parseInt(rawSavedStoreId, 10) : NaN;
      if (Number.isFinite(savedStoreId) && savedStoreId > 0 && stores.some((store) => store.id === savedStoreId)) {
        setSelectedGlobalStoreId(savedStoreId);
      }
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key === GLOBAL_STORE_STORAGE_KEY) {
        syncSelectedStore();
      }
    };

    const handleCustom = () => {
      syncSelectedStore();
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener(GLOBAL_STORE_CHANGE_EVENT, handleCustom as EventListener);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener(GLOBAL_STORE_CHANGE_EVENT, handleCustom as EventListener);
    };
  }, [isSuperAdmin, stores]);

  useEffect(() => {
    const pollPurchaseImportJobs = async () => {
      const jobs = listTrackedPurchaseImportJobs();
      for (const job of jobs) {
        const key = `${job.storeId}:${job.jobId}`;
        if (trackedJobsRef.current.has(key)) continue;
        trackedJobsRef.current.add(key);
        try {
          const result = await getPurchaseImportJob(job.storeId, job.jobId);
          if (result?.status === 'completed') {
            untrackPurchaseImportJob(job.jobId, job.storeId);
            notification.success({
              message: '入库导入已完成',
              description: `已导入 ${result.items_count || result.success_items || result.total_items || job.totalItems || 0} 条记录，订单编号：${result.po_number || '-'}`,
              duration: 6,
              btn: result.created_order_id ? (
                <Button
                  type="link"
                  size="small"
                  onClick={() => navigate(`/purchase/orders/${result.created_order_id}`)}
                >
                  查看订单
                </Button>
              ) : undefined,
            });
          } else if (result?.status === 'failed') {
            untrackPurchaseImportJob(job.jobId, job.storeId);
            notification.error({
              message: '入库导入失败',
              description: result?.error_message || '后台导入任务执行失败',
              duration: 8,
              btn: (
                <Button
                  type="link"
                  size="small"
                  onClick={() => navigate('/purchase/create')}
                >
                  返回入库单
                </Button>
              ),
            });
          }
        } catch (error) {
          console.error('轮询入库导入任务失败:', error);
        } finally {
          trackedJobsRef.current.delete(key);
        }
      }
    };

    const startPolling = () => {
      if (pollingRef.current !== null) return;
      pollPurchaseImportJobs();
      pollingRef.current = window.setInterval(pollPurchaseImportJobs, 3000);
    };

    const stopPolling = () => {
      if (pollingRef.current !== null) {
        window.clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };

    const syncTracker = () => {
      const jobs = listTrackedPurchaseImportJobs();
      if (jobs.length > 0) {
        startPolling();
      } else {
        stopPolling();
      }
    };

    syncTracker();
    window.addEventListener(PURCHASE_IMPORT_JOB_EVENT, syncTracker as EventListener);
    window.addEventListener('focus', syncTracker);

    return () => {
      stopPolling();
      window.removeEventListener(PURCHASE_IMPORT_JOB_EVENT, syncTracker as EventListener);
      window.removeEventListener('focus', syncTracker);
    };
  }, [navigate]);

  // 响应式处理：监听窗口大小变化
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // 在移动设备上默认折叠侧边栏
      if (mobile) {
        setCollapsed(true);
      }
    };

    window.addEventListener('resize', handleResize);
    // 初始化时检查
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const handleGlobalStoreChange = (storeId: number) => {
    if (!Number.isFinite(storeId) || storeId < 0) return;
    setSelectedGlobalStoreId(storeId);
    localStorage.setItem(GLOBAL_STORE_STORAGE_KEY, storeId === 0 ? 'all' : String(storeId));
    window.dispatchEvent(new CustomEvent(GLOBAL_STORE_CHANGE_EVENT, { detail: { value: storeId } }));
  };

  const firstSelectableStoreId = stores.find((store) => store.id > 0)?.id;
  const cashierStoreId =
    Number.isFinite(selectedGlobalStoreId) && (selectedGlobalStoreId || 0) > 0
      ? selectedGlobalStoreId
      : (!isSuperAdmin ? firstSelectableStoreId : undefined);

  const openCashierWindow = (path: 'cashier' | 'cashier2') => (e: React.MouseEvent) => {
    e.preventDefault();

    if (!cashierStoreId) {
      message.warning('请先选择店铺后再进入收银台');
      navigate('/select-store');
      return;
    }

    window.open(
      `${window.location.origin}/stores/${cashierStoreId}/${path}?fs=1`,
      '_blank',
      `noopener,noreferrer,fullscreen=yes,width=${window.screen.availWidth},height=${window.screen.availHeight}`
    );
  };

  const userDropdownItems = {
    items: [
      {
        key: 'profile',
        label: '个人资料',
        icon: <UserOutlined />,
        onClick: () => window.location.assign('http://127.0.0.1:3003/settings'),
      },
      {
        key: 'settings',
        label: '系统设置',
        icon: <SettingOutlined />,
        onClick: () => navigate('/settings'),
      },
      {
        key: 'logout',
        label: '退出登录',
        icon: <LogoutOutlined />,
        onClick: handleLogout,
      },
    ],
  };

  // 生成收银台子菜单项
  const generateCashierSubMenus = () => {
    return [
      {
        key: cashierStoreId ? `/stores/${cashierStoreId}/cashier` : '/cashier',
        label: (
          <span
            style={{ cursor: 'pointer' }}
            onClick={openCashierWindow('cashier')}
          >
            收银台 - 标准版
          </span>
        ),
      },
      {
        key: cashierStoreId ? `/stores/${cashierStoreId}/cashier2` : '/cashier2',
        label: (
          <span
            style={{ cursor: 'pointer' }}
            onClick={openCashierWindow('cashier2')}
          >
            收银台 - 简洁版
          </span>
        ),
      },
    ];
  };

  // 完整菜单定义，pageKey 对应云端 allowed_pages 的 key
  const allMenuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: <Link to="/dashboard">仪表盘</Link>,
      pageKey: null, // 无需授权，始终显示
    },
    {
      key: '/cashier',
      icon: <ShopOutlined />,
      label: '收银台',
      pageKey: 'cashier',
      children: generateCashierSubMenus(),
    },
    {
      key: '/finance',
      icon: <BarChartOutlined />,
      label: '财务',
      pageKey: 'finance',
      children: [
        { key: '/finance/sales', label: <Link to="/finance/sales">销售数据</Link>, pageKey: 'reports' },
        { key: '/finance/receivables', label: <Link to="/finance/receivables">应收账款{receivableAlertCount > 0 && <Badge count={receivableAlertCount} size="small" style={{ marginLeft: 6 }} />}</Link> },
        { key: '/finance/customer-statements', label: <Link to="/finance/customer-statements">客户对账单</Link> },
        { key: '/finance/checks', label: <Link to="/finance/checks">应收支票</Link> },
        { key: '/finance/payables', label: <Link to="/finance/payables">应付账款</Link> },
        { key: '/finance/payable-checks', label: <Link to="/finance/payable-checks">应付支票</Link> },
        { key: '/finance/expenses', label: <Link to="/finance/expenses">费用管理</Link> },
        { key: '/finance/profit', label: <Link to="/finance/profit">盈利报表</Link>, pageKey: 'reports' },
        { key: '/finance/cashflow', label: <Link to="/finance/cashflow">现金流</Link> },
        { key: '/finance/prepayments', label: <Link to="/finance/prepayments">预付款</Link> },
        { key: '/finance/cashier-settlement', label: <Link to="/finance/cashier-settlement">收银结算</Link> },
        { key: '/finance/stock-diff', label: <Link to="/finance/stock-diff">库存差异</Link> },
      ],
    },
    {
      key: '/customers',
      icon: <ContactsOutlined />,
      label: <Link to="/customers">店铺客户</Link>,
      pageKey: 'customers',
    },
    {
      key: '/members',
      icon: <TrophyOutlined />,
      label: '会员管理',
      pageKey: 'members',
      children: [
        { key: '/members', icon: <UserOutlined />, label: <Link to="/members">会员列表</Link> },
        { key: '/promotions', icon: <GiftOutlined />, label: <Link to="/promotions">促销活动</Link> },
        { key: '/coupons', icon: <GiftOutlined />, label: <Link to="/coupons">优惠券管理</Link> },
        { key: '/points-settings', icon: <SettingOutlined />, label: <Link to="/points-settings">积分设置</Link> },
      ],
    },
    {
      key: '/products-group',
      icon: <ShoppingOutlined />,
      label: '产品管理',
      pageKey: 'inventory',
      children: [
        { key: '/products', icon: <ShoppingOutlined />, label: <Link to="/products">商品列表</Link> },
        { key: '/product-categories', icon: <ProfileOutlined />, label: <Link to="/product-categories">商品类别</Link> },
        { key: '/tax-categories', icon: <PercentageOutlined />, label: <Link to="/tax-categories">税率类别</Link> },
        { key: '/suppliers', icon: <TeamOutlined />, label: <Link to="/suppliers">供应商管理</Link> },
      ],
    },
    {
      key: '/stock',
      icon: <InboxOutlined />,
      label: (
        <span>
          库存管理
          {lowStockCount > 0 && (
            <Badge count={lowStockCount} size="small" style={{ marginLeft: 8 }} />
          )}
        </span>
      ),
      pageKey: 'inventory',
      children: [
        { key: '/stock/overview', label: <Link to="/stock/overview">库存列表</Link> },
        { key: '/stock/low-stock', label: <Link to="/stock/low-stock">低库存预警</Link> },
        { key: '/stock/adjust', label: <Link to="/stock/adjust">库存调整</Link> },
        { key: '/stock/new-stock', label: <Link to="/stock/new-stock">备品仓管理</Link> },
        { key: '/stock/stocktaking', label: <Link to="/stock/stocktaking">仓库盘点</Link>, pageKey: 'stocktaking' },
        { key: '/stock/transfer', label: <Link to="/stock/transfer">店铺调拨</Link> },
      ],
    },
    {
      key: '/procurement',
      icon: <FileTextOutlined />,
      label: '采购管理',
      pageKey: 'purchase',
      children: [
        { key: '/procurement/orders', label: <Link to="/procurement/orders">采购单列表</Link> },
        { key: '/procurement/orders/create', label: <Link to="/procurement/orders/create">创建采购单</Link> },
      ],
    },
    {
      key: '/purchase',
      icon: <ShoppingCartOutlined />,
      label: '入库管理',
      pageKey: 'purchase',
      children: [
        { key: '/purchase/orders', label: <Link to="/purchase/orders">入库列表</Link> },
        { key: '/purchase/create', label: <Link to="/purchase/create">入库单</Link> },
      ],
    },
    {
      key: '/transport',
      icon: <CarOutlined />,
      label: '运输管理',
      pageKey: null,
      children: [
        { key: '/transport/list', label: <Link to="/transport/list">配送商管理</Link> },
        { key: '/transport/mode', label: <Link to="/transport/mode">运输方式管理</Link> },
      ],
    },
    {
      key: '/yg-orders',
      icon: <FileSearchOutlined />,
      label: '友购订单',
      pageKey: 'orders',
      children: [
        { key: '/yg-orders', label: <Link to="/yg-orders">订单查看</Link> },
        { key: '/yg-vendedor', label: <Link to="/yg-vendedor">业务员管理</Link> },
      ],
    },
    {
      key: '/system',
      icon: <SettingOutlined />,
      label: '基本参数设置',
      pageKey: null, // 系统管理本身始终显示，内部子项各自控制
      children: [
        { key: '/remote-clients', icon: <CloudOutlined />, label: <Link to="/remote-clients">远程终端管理</Link>, pageKey: null, roleKey: 'super_admin' },
        { key: '/terminals', icon: <MobileOutlined />, label: <Link to="/terminals">终端设备管理</Link>, pageKey: null, roleKey: 'super_admin' },
        { key: '/users', icon: <UsergroupAddOutlined />, label: <Link to="/users">用户管理</Link>, pageKey: null, roleKey: 'super_admin' },
        { key: '/audit-logs', icon: <FileSearchOutlined />, label: <Link to="/audit-logs">操作审计</Link>, pageKey: null, roleKey: 'super_admin' },
        { key: '/backup', icon: <DatabaseOutlined />, label: <Link to="/backup">数据备份</Link>, pageKey: null, roleKey: 'super_admin' },
        { key: '/receipt-template-editor', icon: <ProfileOutlined />, label: <Link to="/receipt-template-editor">小票模板</Link>, pageKey: 'settings', roleKey: 'super_admin' },
        { key: '/printer-share', icon: <PrinterOutlined />, label: <Link to="/printer-share">打印机共享</Link>, pageKey: null, roleKey: 'super_admin' },
        { key: '/whatsapp', icon: <WhatsAppOutlined />, label: <Link to="/whatsapp">WhatsApp管理</Link>, pageKey: null, roleKey: ['super_admin', 'store_owner', 'admin'] },
        { key: '/settings', icon: <SettingOutlined />, label: <Link to="/settings">系统参数设置</Link>, pageKey: null },
      ],
    },
  ];

  // 根据 allowedPages 和角色过滤菜单（pageKey=null 表示无需云控授权；roleKey 指定仅限该角色可见）
  const currentRoleCode = getUserRoleCode(user);
  const menuItems = allMenuItems
    .filter(item => item.pageKey === null || isPageAllowed(item.pageKey, allowedPages))
    .map(item => {
      if (!('children' in item) || !item.children) return item;
      // 过滤子菜单：按 pageKey（云控）和 roleKey（角色限制）双重过滤
      const filteredChildren = item.children.filter((child: any) => {
        const pageOk = child.pageKey === undefined || child.pageKey === null || isPageAllowed(child.pageKey, allowedPages);
        const roleOk = Array.isArray(child.roleKey)
          ? hasAnyRole(currentRoleCode, child.roleKey)
          : (!child.roleKey || hasAnyRole(currentRoleCode, [child.roleKey]));
        return pageOk && roleOk;
      });
      return { ...item, children: filteredChildren };
    });

  const renderedMenuItems: MenuProps['items'] = buildSafeMenuItems(
    menuItems.map(item => {
      if (item.key === '/stock') {
        return {
          ...item,
          label: (
            <span>
              库存管理
              {lowStockCount > 0 && (
                <Badge count={lowStockCount} size="small" style={{ marginLeft: 8 }} />
              )}
            </span>
          ),
        };
      }
      return item;
    }) as GenericMenuItem[]
  );

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* 移动端遮罩层 */}
      {isMobile && !collapsed && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.45)',
            zIndex: 998,
          }}
          onClick={() => setCollapsed(true)}
        />
      )}
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={260}
        breakpoint="lg"
        collapsedWidth={isMobile ? 0 : 80}
        style={{
          overflow: 'hidden',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: isMobile && !collapsed ? 999 : 100,
          background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
          boxShadow: '4px 0 24px rgba(0,0,0,0.15)',
          borderRight: '1px solid rgba(255,255,255,0.05)',
        }}
      >
        <style>
          {`
            .ant-menu.ant-menu-dark {
              background: transparent !important;
            }
            .ant-menu-dark .ant-menu-item, .ant-menu-dark .ant-menu-submenu-title {
              margin: 4px 12px !important;
              width: calc(100% - 24px) !important;
              border-radius: 12px !important;
              height: 48px !important;
              line-height: 48px !important;
              transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
            }
            .ant-menu-dark .ant-menu-item-selected {
              background: linear-gradient(90deg, #1D44E9 0%, #3b82f6 100%) !important;
              box-shadow: 0 8px 16px rgba(29, 68, 233, 0.25) !important;
              color: white !important;
            }
            .ant-menu-dark .ant-menu-item:hover, .ant-menu-dark .ant-menu-submenu-title:hover {
              background: rgba(255, 255, 255, 0.08) !important;
              transform: translateX(4px);
            }
            .ant-menu-dark .ant-menu-item .anticon, .ant-menu-dark .ant-menu-submenu-title .anticon {
              font-size: 18px !important;
            }
            .ant-layout-sider-children {
              display: flex;
              flex-direction: column;
              height: 100%;
              overflow-y: auto;
              overflow-x: hidden;
            }
            .ant-menu-item-label {
              font-weight: 500;
            }
            /* 自定义滚动条 */
            .ant-layout-sider-children::-webkit-scrollbar {
              width: 4px;
            }
            .ant-layout-sider-children::-webkit-scrollbar-thumb {
              background: rgba(255,255,255,0.1);
              border-radius: 10px;
            }
          `}
        </style>
        <div style={{ 
          height: 80, 
          margin: '0 0 16px 0', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          gap: 12, 
          overflow: 'hidden',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
          padding: '0 16px'
        }}>
          <img 
            src="/wk-logo.jpg" 
            alt="Logo" 
            style={{ 
              width: collapsed ? 40 : 32, 
              height: collapsed ? 40 : 32, 
              borderRadius: 10,
              objectFit: 'cover',
              boxShadow: '0 8px 20px rgba(0,0,0,0.4)',
              flexShrink: 0,
              transition: 'all 0.3s ease'
            }} 
          />
          {!collapsed && (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <h1 style={{ 
                color: 'white', 
                margin: 0, 
                fontSize: 18, 
                fontWeight: 800,
                letterSpacing: 1,
                whiteSpace: 'nowrap',
                lineHeight: 1.2
              }}>
                ERP管理系统
              </h1>
              <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: 10, fontWeight: 600, letterSpacing: 1 }}>NEXT GEN PLATFORM</span>
            </div>
          )}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={renderedMenuItems}
        />
      </Sider>
      <Layout style={{ marginLeft: isMobile ? 0 : (collapsed ? 80 : 250), transition: 'margin-left 0.2s', minHeight: '100vh' }}>
        <Header style={{ 
          padding: 0, 
          backgroundColor: 'rgba(255, 255, 255, 0.8)', 
          position: 'sticky', 
          top: 0, 
          zIndex: 10, 
          boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(226, 232, 240, 0.8)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: isMobile ? '0 12px' : '0 24px' }}>
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{ 
                fontSize: '18px', 
                width: 56, 
                height: 56, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                color: '#64748b',
                transition: 'all 0.3s'
              }}
              className="hover-scale"
            />
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {stores.length > 0 && (
                <Select
                  value={selectedGlobalStoreId}
                  onChange={handleGlobalStoreChange}
                  style={{ width: isMobile ? 130 : 180, marginRight: 12 }}
                  size={isMobile ? 'small' : 'middle'}
                  options={stores.map((store) => ({ value: store.id, label: store.name || `店铺 ${store.id}` }))}
                  placeholder="选择店铺"
                  disabled={isSuperAdmin ? false : stores.length <= 1}
                />
              )}
              <Badge count={lowStockCount} dot>
                <Button
                  type="text"
                  icon={<BellOutlined style={{ fontSize: 18 }} />}
                  onClick={() => navigate('/stock/low-stock')}
                  style={{ marginRight: 16 }}
                />
              </Badge>
              <Dropdown menu={userDropdownItems} placement="bottomRight">
                <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                  <Avatar icon={<UserOutlined />} />
                  {!isMobile && <span style={{ marginLeft: 8 }}>{user?.username || '用户'}</span>}
                </div>
              </Dropdown>
            </div>
          </div>
        </Header>
        <Content
          style={{
            margin: isMobile ? '12px 8px' : '24px 16px',
            padding: isMobile ? 12 : 24,
            minHeight: 280,
            background: colorToken.colorBgContainer,
            borderRadius: isMobile ? 4 : 8,
            overflow: 'auto',
            position: 'relative',
            zIndex: 1,
          }}
        >
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
